import React, { useState, useEffect, useReducer, useContext } from "react";
import openSocket from "../../services/socket-io";

import { makeStyles } from "@material-ui/core/styles";
import List from "@material-ui/core/List";
import Paper from "@material-ui/core/Paper";

import TicketListItem from "../TicketListItem";
import TicketsListSkeleton from "../TicketsListSkeleton";

import useTickets from "../../hooks/useTickets";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SettingsContext } from "../../context/Settings/SettingsContext";

const useStyles = makeStyles(theme => ({
	ticketsListWrapper: {
		position: "relative",
		display: "flex",
		height: "100%",
		flexDirection: "column",
		overflow: "hidden",
		borderTopRightRadius: 0,
		borderBottomRightRadius: 0,
		background: "transparent",
	},

	ticketsList: {
		flex: 1,
		overflowY: "scroll",
		...theme.scrollbarStyles,
		paddingLeft: 10,
		paddingBottom: 99,
		background: "transparent",
	},

	ticketsListHeader: {
		zIndex: 2,
		background: "transparent",
		borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
	},

	ticketsCount: {
		fontWeight: "normal",
		color: "rgb(104, 121, 146)",
		marginLeft: "8px",
		fontSize: "14px",
	},

	noTicketsText: {
		textAlign: "center",
		color: "rgb(104, 121, 146)",
		fontSize: "14px",
		lineHeight: "1.4",
	},

	noTicketsTitle: {
		textAlign: "center",
		fontSize: "16px",
		fontWeight: "600",
		margin: "0px",
	},

	noTicketsDiv: {
		display: "flex",
		height: "100px",
		margin: 40,
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
	},
}));

const reducer = (state, action) => {

	const userData = action.userData;

	if (action.type === "LOAD_TICKETS") {
		const newTickets = action.payload;

		if (newTickets.length === 0) return [];

		newTickets.forEach(ticket => {
			const ticketIndex = state.findIndex(t => t.id === ticket.id);
			if (ticketIndex !== -1) {

				state[ticketIndex] = ticket;

				if (ticket.unreadMessages > 0) {
					state.unshift(state.splice(ticketIndex, 1)[0]);
				}
			} else {
				state.push(ticket);
			}
		});

		if (userData.profile !== "admin" && userData.profile !== "super") {
			state = state.filter((t) => (t.userId === userData.userID || t.userId === null));
		}

		return [...state];
	}

	if (action.type === "RESET_UNREAD") {
		const ticketId = action.payload;

		const ticketIndex = state.findIndex(t => t.id === ticketId);
		if (ticketIndex !== -1) {
			state[ticketIndex].unreadMessages = 0;
		}

		return [...state];
	}

	if (action.type === "UPDATE_TICKET") {
		const ticket = action.payload;

		const ticketIndex = state.findIndex(t => t.id === ticket.id);
		if (ticketIndex !== -1) {
			state[ticketIndex] = ticket;
		} else {
			state.unshift(ticket);
		}

		if (userData.profile !== "admin" && userData.profile !== "super") {
			state = state.filter((t) => (t.userId === userData.userID || t.userId === null));
		}

		return [...state];
	}

	if (action.type === "UPDATE_TICKET_UNREAD_MESSAGES") {
		const ticket = action.payload;

		const ticketIndex = state.findIndex(t => t.id === ticket.id);
		if (ticketIndex !== -1) {
			state[ticketIndex] = ticket;
			state.unshift(state.splice(ticketIndex, 1)[0]);
		} else {
			state.unshift(ticket);
		}


		return [...state];
	}

	if (action.type === "UPDATE_TICKET_CONTACT") {
		const contact = action.payload;

		const ticketIndex = state.findIndex(t => t.contactId === contact.id);
		if (ticketIndex !== -1) {
			state[ticketIndex].contact = contact;
		}

		if (userData.profile !== "admin" && userData.profile !== "super") {
			state = state.filter((t) => (t.userId === userData.userID || t.userId === null));
		}

		return [...state];
	}

	if (action.type === "DELETE_TICKET") {
		const ticketId = action.payload;
		const ticketIndex = state.findIndex(t => t.id === ticketId);
		if (ticketIndex !== -1) {
			state.splice(ticketIndex, 1);
		}

		return [...state];
	}

	if (action.type === "RESET") {
		return [];
	}
};

const TicketsList = ({ onSetTabOpen, status, searchParam, isSearch, tags, users, showAll, selectedQueueIds, updateCount, style }) => {
	// const { onSetTabOpen, status, searchParam, showAll, selectedQueueIds, updateCount, style } =	props;
	const classes = useStyles();
	const [pageNumber, setPageNumber] = useState(1);
	const [ticketsList, dispatch] = useReducer(reducer, []);
	const { user } = useContext(AuthContext);
	const { profile, queues } = user;
	const { settings } = useContext(SettingsContext);
	const [queuedTicketsOnly, setQueuedTicketsOnly] = useState("disable");

	useEffect(() => {
		const loadsettings = async () => {
		  const queuedTicketsOnly = await settings.find(t => t.key === "queuedTicketsOnly");
		  setQueuedTicketsOnly(queuedTicketsOnly?.value);
		}
		loadsettings();
	}, [settings]);


	useEffect(() => {
		dispatch({ type: "RESET" });
		setPageNumber(1);
	}, [status, searchParam, dispatch, showAll, tags, users, selectedQueueIds]);

	const { tickets, hasMore, loading } = useTickets({
		isSearch,
		queues,
		pageNumber,
		searchParam,
		status,
		showAll,
		tags: JSON.stringify(tags),
		users: JSON.stringify(users),
		queueIds: JSON.stringify(selectedQueueIds),
	});

	useEffect(() => {

		const fetchTickets = async () => {

			if (profile.toUpperCase() !== "USER" || queuedTicketsOnly === "disabled") {
				dispatch({ type: "LOAD_TICKETS", payload: tickets, userData: { userID: user.id, profile: user.profile } });
			} else {
				if (queuedTicketsOnly === "enabled") {
					const queueIds = queues.map((q) => q.id);
					let filteredTickets = tickets.filter((t) => queueIds.indexOf(t.queueId) > -1);
					filteredTickets = tickets.filter((t) => t.isBot !== true);

					dispatch({ type: "LOAD_TICKETS", payload: filteredTickets, userData: { userID: user.id, profile: user.profile } });
				}
			}
		}
		fetchTickets();

	}, [tickets, queues, profile, user, queuedTicketsOnly]);


	useEffect(() => {
		const socket = openSocket();

		const shouldUpdateTicket = ticket =>
			(!ticket.userId || ticket.userId === user?.id || showAll) &&
			(!ticket.queueId || selectedQueueIds.indexOf(ticket.queueId) > -1);

		const notBelongsToUserQueues = ticket =>
			ticket.queueId && selectedQueueIds.indexOf(ticket.queueId) === -1;

		socket.on("connect", () => {
			if (status) {
				socket.emit("joinTickets", status);
			} else {
				socket.emit("joinNotification");
			}
		});

		socket.on("ticket", async data => {

			if (data.action === "updateUnread") {
				dispatch({
					type: "RESET_UNREAD",
					payload: data.ticketId,
					userData: { userID: user.id, profile: user.profile }
				});
			}

			if (data.action === "update" && shouldUpdateTicket(data.ticket)) {

				if (profile.toUpperCase() !== "USER" || queuedTicketsOnly === "disabled") {
					dispatch({ type: "UPDATE_TICKET", payload: data.ticket, userData: { userID: user.id, profile: user.profile } });
				} else {
					// CONFIGURAÇÕES PARA USUÁRIOS
					if (data.ticket.isBot && queuedTicketsOnly === "enabled") {
						dispatch({ type: "DELETE_TICKET", payload: data.ticket.id, userData: { userID: user.id, profile: user.profile } });
					} else {
						dispatch({ type: "UPDATE_TICKET", payload: data.ticket, userData: { userID: user.id, profile: user.profile } });
					}

				}

			}

			if (data.action === "update" && notBelongsToUserQueues(data.ticket)) {
				dispatch({ type: "DELETE_TICKET", payload: data.ticket.id, userData: { userID: user.id, profile: user.profile } });
			}

			if (data.action === "delete") {
				dispatch({ type: "DELETE_TICKET", payload: data.ticketId, userData: { userID: user.id, profile: user.profile } });
			}
		});

		socket.on("appMessage", async data => {
			if (data.action === "create" && shouldUpdateTicket(data.ticket)) {

				dispatch({
					type: "UPDATE_TICKET_UNREAD_MESSAGES",
					payload: data.ticket,
					userData: { userID: user.id, profile: user.profile }
				});

				if (profile.toUpperCase() === "USER") {
					// CONFIGURAÇÕES PARA USUÁRIOS
					if (data.ticket.isBot && queuedTicketsOnly === "enabled") {
						dispatch({ type: "DELETE_TICKET", payload: data.ticket.id, userData: { userID: user.id, profile: user.profile } });
					}
				}

			}
		});

		socket.on("contact", data => {
			if (data.action === "update") {
				dispatch({
					type: "UPDATE_TICKET_CONTACT",
					payload: data.contact,
					userData: { userID: user.id, profile: user.profile }
				});
			}
		});
		socket.on("contact", data => {
			if (data.action === "update") {
				dispatch({
					type: "UPDATE_TICKET_CONTACT",
					payload: data.contact,
					userData: { userID: user.id, profile: user.profile }
				});
			}
		});

		return () => {
			socket.disconnect();
		};
	}, [status, showAll, user, users, selectedQueueIds, profile, queuedTicketsOnly]);

	useEffect(() => {
		if (typeof updateCount === "function") {
			updateCount(ticketsList.length);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ticketsList]);

	const loadMore = () => {
		setPageNumber(prevState => prevState + 1);
	};

	const handleScroll = e => {
		if (!hasMore || loading) return;

		const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

		if (scrollHeight - (scrollTop + 100) < clientHeight) {
			loadMore();
		}
	};

	return (
		<Paper className={classes.ticketsListWrapper} style={style}>
			<Paper
				square
				name="closed"
				elevation={0}
				className={classes.ticketsList}
				onScroll={handleScroll}
			>
				<List style={{ paddingTop: 0 }}>
					{ticketsList.length === 0 ? (
						<div className={classes.noTicketsDiv}>
							<span className={classes.noTicketsTitle}>
								{i18n.t("ticketsList.noTicketsTitle")}
							</span>
							<p className={classes.noTicketsText}>
								{i18n.t("ticketsList.noTicketsMessage")}
							</p>
						</div>
					) : (
						<>
							{ticketsList.map(ticket => (
								<TicketListItem onSetTabOpen={onSetTabOpen} ticket={ticket} key={ticket.id} />
							))}
						</>
					)}
					{loading && <TicketsListSkeleton />}
				</List>
			</Paper>
		</Paper>
	);
};

export default TicketsList;
